// AgentDetailDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Settings } from 'lucide-react';

/**
 * Dialog for viewing AI agent details
 * 
 * @param {Object} props
 * @param {Object|null} props.agent - The selected agent to display (null = closed)
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {function} props.onEdit - Callback when Edit button is clicked
 */
export const AgentDetailDialog = ({
  agent,
  onClose,
  onEdit
}) => {
  return (
    <Dialog open={!!agent} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-violet-100 text-violet-700">
                {agent?.name?.[0]}
              </AvatarFallback>
            </Avatar>
            {agent?.name} - {agent?.role_title}
          </DialogTitle>
        </DialogHeader>
        
        {agent && (
          <div className="space-y-4">
            <div>
              <Label>Mission</Label>
              <p className="text-sm text-muted-foreground">{agent.mission}</p>
            </div>
            
            <div>
              <Label>Primary Queues</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {agent.primary_queues?.map((q, i) => (
                  <Badge key={i} variant="outline">{q}</Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Automates Well</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {agent.automates_well?.map((s, i) => (
                  <Badge key={i} className="bg-violet-50 text-violet-700">{s}</Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label>AI Configuration</Label>
              <div className="grid grid-cols-3 gap-2 mt-1 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <p className="text-xs text-muted-foreground">Left Brain (Analytical)</p>
                  <p className="font-medium">{agent.ai_config?.left_brain_model || 'gpt-4o'}</p>
                  <p className="text-xs text-blue-600">{agent.ai_config?.left_brain_provider || 'openai'}</p>
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <p className="text-xs text-muted-foreground">Right Brain (Creative)</p>
                  <p className="font-medium">{agent.ai_config?.right_brain_model || 'claude-sonnet-4-5-20250929'}</p>
                  <p className="text-xs text-purple-600">{agent.ai_config?.right_brain_provider || 'anthropic'}</p>
                </div>
                <div className="bg-emerald-50 p-2 rounded">
                  <p className="text-xs text-muted-foreground">Brain Stem (Synthesis)</p>
                  <p className="font-medium">{agent.ai_config?.brain_stem_model || 'gemini-2.5-flash'}</p>
                  <p className="text-xs text-emerald-600">{agent.ai_config?.brain_stem_provider || 'gemini'}</p>
                </div>
              </div>
            </div>
            
            <div>
              <Label>KPIs</Label>
              <div className="space-y-2 mt-1">
                {agent.kpis?.map((kpi, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{kpi.name}</span>
                    <span className="font-medium">{kpi.target} {kpi.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="agent-detail-close">
            Close
          </Button>
          <Button onClick={() => onEdit(agent)} data-testid="agent-detail-edit">
            <Settings className="h-4 w-4 mr-2" />
            Edit Agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentDetailDialog;
